import {
  addDays,
  differenceInHours,
  format,
  isBefore,
  isWithinInterval,
} from "date-fns";
import { useCallback, useMemo } from "react";
import { OperatingHours } from "utils";
import { Day, DayInfo } from "../redux/services/config";
import { convertTimeToDate } from "../utils/convertTimeToDate";
import { formatTime } from "./formatTime";
import { useTranslation } from "react-i18next";

export enum Status {
  OPEN = "open",
  CLOSED = "close",
  CLOSED_UNTIL = "closed_until",
  OPEN_24_HOURS = "open_24_hours",
  TEMP_CLOSED = "temporarily_closed",
}

export const useCalculateOperatingHours = (operatingHours: OperatingHours) => {
  const currentDate = useMemo(() => new Date(), []);
  const { t } = useTranslation();

  const todaysDay = format(currentDate, "eeee").toLowerCase() as Day;
  const todaysOpeningHours = operatingHours && operatingHours[todaysDay];

  const checkIsActiveOpeningTime = useCallback(
    (dayOperatingHours: DayInfo) => {
      if (dayOperatingHours === null) return false;
      const openingTime = convertTimeToDate(dayOperatingHours.opening_time);
      const closingTime = convertTimeToDate(dayOperatingHours.closing_time);

      // if closing time is before opening time, then it's open overnight and we need to account for 2 intervals
      if (closingTime < openingTime) {
        return !isWithinInterval(currentDate, {
          start: closingTime,
          end: openingTime,
        });
      }

      return isWithinInterval(currentDate, {
        start: openingTime,
        end: closingTime,
      });
    },
    [currentDate],
  );

  const status = useMemo(() => {
    if (!todaysOpeningHours && todaysOpeningHours === null)
      return Status.CLOSED;

    const openingTime = convertTimeToDate(todaysOpeningHours.opening_time);
    const closingTime = convertTimeToDate(todaysOpeningHours.closing_time);

    if (differenceInHours(closingTime, openingTime) === 24) {
      return Status.OPEN_24_HOURS;
    }

    if (isBefore(currentDate, openingTime)) return Status.CLOSED;

    return Status.OPEN;
  }, [todaysOpeningHours, currentDate]);

  const getNextOpeningTime = useCallback(() => {
    if (todaysOpeningHours) {
      isBefore(currentDate, convertTimeToDate(todaysOpeningHours.opening_time));

      return {
        day: todaysDay,
        time: todaysOpeningHours.opening_time,
      };
    }

    // Loop through the next 7 days to find the next available opening time
    for (let i = 1; i <= 7; i++) {
      const nextDay = addDays(currentDate, i);
      const day = format(nextDay, "eeee").toLowerCase() as Day;
      const nextDayOperatingHours = operatingHours && operatingHours[day];

      if (nextDayOperatingHours) {
        return {
          day,
          time: nextDayOperatingHours.opening_time,
        };
      }
    }
  }, [currentDate, operatingHours, todaysOpeningHours, todaysDay]);

  const statusCopy = useMemo(() => {
    if (operatingHours === null) return null;
    switch (status) {
      case Status.OPEN_24_HOURS:
        return t("use_calculate_opening_hours.open_24_hours");
      case Status.OPEN:
        if (todaysOpeningHours) {
          return t("use_calculate_opening_hours.open_until", {
            time: formatTime(todaysOpeningHours.closing_time),
          });
        }
        break;
      case Status.CLOSED: {
        const nextOpening = getNextOpeningTime();
        if (!nextOpening) return "";

        return t("use_calculate_opening_hours.closed_until", {
          time: formatTime(nextOpening?.time!),
        });
      }
      default:
        break;
    }
  }, [status, todaysOpeningHours, getNextOpeningTime, t, operatingHours]);

  return {
    status,
    statusCopy,
    todaysDay,
    checkIsActiveOpeningTime,
  };
};
